import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 7: "Selection Sort" (Master Spec Section 5), campaign
 * levels 61-70. Teaches: unsorted region, current minimum/maximum, scanning, selecting candidate, placing selected item, shrinking unsorted region.
 * Algorithm(s) used in this world: selection-sort.
 */
export const world07SelectionSortLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-061",
    world: 7,
    levelInWorld: 1,
    globalLevelNumber: 61,
    algorithm: "selection-sort",
    values: [7, 2, 9, 4, 6],
    sortDirection: "ascending",
    learningObjectives: [
      "Learn Selection Sort: scan the unsorted region to find the current minimum, then place it.",
      "See the sorted region grow from the front of the list, one placement at a time.",
    ],
    notes: "A first practice run: scan, select the smallest remaining value, place it at the front.",
  }),
  createCampaignLevel({
    id: "campaign-062",
    world: 7,
    levelInWorld: 2,
    globalLevelNumber: 62,
    algorithm: "selection-sort",
    values: [7, 2, 9, 4, 6],
    sortDirection: "descending",
    learningObjectives: [
      "Apply the same scan-select-place idea while tracking the current maximum instead of the minimum.",
      "Compare which value gets selected first here versus in level 61's ascending version.",
    ],
    notes: "Same five values as level 61 -- this time the largest remaining value is selected each round.",
  }),
  createCampaignLevel({
    id: "campaign-063",
    world: 7,
    levelInWorld: 3,
    globalLevelNumber: 63,
    algorithm: "selection-sort",
    values: [11, 3, 15, 7, 9, 1],
    sortDirection: "ascending",
    learningObjectives: [
      "Practice a full scan of the unsorted region before making any placement decision.",
      "See the unsorted region shrink by exactly one item after each placement.",
    ],
    notes: "A 6-item unsorted list -- one full scan-select-place round per remaining item.",
  }),
  createCampaignLevel({
    id: "campaign-064",
    world: 7,
    levelInWorld: 4,
    globalLevelNumber: 64,
    algorithm: "selection-sort",
    values: [11, 3, 15, 7, 9, 1],
    sortDirection: "descending",
    learningObjectives: [
      "Sort the same six values as level 63 by repeatedly selecting the current maximum.",
      "Reinforce that the scanning process itself doesn't change -- only what counts as best.",
    ],
    notes: "Same values as level 63, sorted largest to smallest.",
  }),
  createCampaignLevel({
    id: "campaign-065",
    world: 7,
    levelInWorld: 5,
    globalLevelNumber: 65,
    algorithm: "selection-sort",
    values: [2, 4, 6, 8, 10, 12],
    sortDirection: "ascending",
    learningObjectives: [
      "See that Selection Sort still scans the entire unsorted region every round, even on sorted data.",
      "Contrast this with Bubble Sort's early termination -- Selection Sort has no equivalent shortcut.",
    ],
    notes: "Already sorted -- but Selection Sort still performs a full scan each round to confirm the current minimum.",
  }),
  createCampaignLevel({
    id: "campaign-066",
    world: 7,
    levelInWorld: 6,
    globalLevelNumber: 66,
    algorithm: "selection-sort",
    values: [19, 4, 27, 11, 8, 15, 2],
    sortDirection: "ascending",
    learningObjectives: [
      "Apply scan-select-place across more rounds as the dataset grows.",
      "Track exactly which items remain in the unsorted region as rounds progress.",
    ],
    notes: "A 7-item unsorted list -- one more round than level 63's dataset.",
  }),
  createCampaignLevel({
    id: "campaign-067",
    world: 7,
    levelInWorld: 7,
    globalLevelNumber: 67,
    algorithm: "selection-sort",
    values: [19, 4, 27, 11, 8, 15, 2],
    sortDirection: "descending",
    learningObjectives: [
      "Sort the same seven values as level 66 by maximum-selection instead of minimum-selection.",
      "Build fluency switching the selection criterion between rounds of practice.",
    ],
    notes: "Same values as level 66, descending goal.",
  }),
  createCampaignLevel({
    id: "campaign-068",
    world: 7,
    levelInWorld: 8,
    globalLevelNumber: 68,
    algorithm: "selection-sort",
    values: [29, 24, 19, 14, 9, 4, 1],
    sortDirection: "ascending",
    learningObjectives: [
      "See Selection Sort handle a fully reverse-ordered dataset just as directly as any other.",
      "Notice the number of comparisons per round stays the same regardless of the starting order.",
    ],
    notes: "Exactly reverse-ordered -- unlike Bubble Sort, this does not change how many scans Selection Sort needs.",
  }),
  createCampaignLevel({
    id: "campaign-069",
    world: 7,
    levelInWorld: 9,
    globalLevelNumber: 69,
    algorithm: "selection-sort",
    values: [33, 7, 21, 14, 2, 28, 9, 17],
    sortDirection: "ascending",
    learningObjectives: [
      "Complete a full ascending sort on the largest dataset in this world.",
      "Consolidate unsorted-region scanning, candidate tracking, and shrinking the unsorted region.",
    ],
    notes: "World 7's largest dataset -- every round narrows the unsorted region by one item.",
  }),
  createCampaignLevel({
    id: "campaign-070",
    world: 7,
    levelInWorld: 10,
    globalLevelNumber: 70,
    algorithm: "selection-sort",
    values: [33, 7, 21, 14, 2, 28, 9, 17],
    sortDirection: "descending",
    learningObjectives: [
      "Complete the same dataset as level 69 sorted from largest to smallest.",
      "Reflect on how Selection Sort compares to Bubble Sort before moving to Insertion Sort.",
    ],
    notes: "World 7 capstone -- same eight values as level 69, sorted in the opposite direction.",
  }),
];
